import { AttachmentFile, AttachmentFileType } from "@inrev/common";
import { ReactNode } from "react";
import { trackAttachmentAction } from "../../analytics";
import { useGetFilesAsZip, useGetFileUrl } from "../../api";
import { ClickableIcon, Spinner, Text } from "../primitives";
import { AttachmentUpload, AttachmentUploadProps } from "../ui/Upload";

type AttachmentSectionProps<
	TFileType extends AttachmentFileType = AttachmentFileType,
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
	TAllowedType extends Extract<TFileType, keyof TAllowedTypesAndLabels> = Extract<
		TFileType,
		keyof TAllowedTypesAndLabels
	>,
	TRequired extends TAllowedType[] | undefined = TAllowedType[] | undefined,
> = {
	filter?: (attachment: AttachmentFile<TFileType>) => boolean;
	attachments: AttachmentFile<TFileType>[];
	upload: Omit<
		AttachmentUploadProps<TFileType, TAllowedTypesAndLabels, TAllowedType, TRequired>,
		"value"
	>;
	download?: {
		baseUrl: string;
		baseQueryKey: string;
	};
	downloadAllName?: string;
	updateTypes?: Omit<
		AttachmentUploadProps<TFileType, TAllowedTypesAndLabels, TAllowedType, TRequired>,
		"value"
	>;
	expandedByDefault?: boolean;
	allowTypeConfirmation?: boolean;
	showTabs?: boolean;
	allowDelete?: boolean;
	header?: string;
	subHeader?: ReactNode;
	allowUpload?: boolean;
};

export const AttachmentSection = <
	TFileType extends AttachmentFileType = AttachmentFileType,
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
	TAllowedType extends Extract<TFileType, keyof TAllowedTypesAndLabels> = Extract<
		TFileType,
		keyof TAllowedTypesAndLabels
	>,
	TRequired extends TAllowedType[] | undefined = TAllowedType[] | undefined,
>({
	filter,
	attachments,
	upload,
	download,
	downloadAllName,
	allowDelete,
	showTabs,
	header = "Attachments",
	allowTypeConfirmation,
	subHeader,
	expandedByDefault,
	allowUpload,
}: AttachmentSectionProps<TFileType, TAllowedTypesAndLabels, TAllowedType, TRequired>) => {
	const { getFileUrl } = useGetFileUrl();
	const { getFilesAsZip, filesAsZipLoading } = useGetFilesAsZip();

	const getAllDownloadUrls = () => {
		if (!download) return;
		// A programmatic blob download, so the browser never emits a File Downloaded
		// event for it -- see ANALYTICS.md.
		trackAttachmentAction("download_all", upload.analyticsContext);
		const files = attachments.map((attachment) => ({
			fileName: attachment.file.name,
			extension: attachment.file.extension,
			url: `${download.baseUrl}/${attachment.id}`,
		}));
		getFilesAsZip({
			files,
			queryKey: [`${download.baseQueryKey}-all`],
			zippedFileName: downloadAllName ?? "Attachments",
		});
	};

	return (
		<div id="attachments" className="flex flex-col w-full space-y-[28px]">
			<div className="flex items-center w-full h-[28px]">
				<Text type="title" color="primary" className="flex-1">
					{header}
				</Text>
				{attachments.length > 0 && (
					<>
						{filesAsZipLoading ? (
							<div className="h-[28px] w-[28px] flex items-center justify-center">
								<Spinner size="lg" />
							</div>
						) : (
							<ClickableIcon
								type="arrow-down-tray"
								size="small"
								color="secondary"
								aria-label="Download all attachments"
								trackingId={`${upload.trackingPrefix}-download-all`}
								onClick={() => getAllDownloadUrls()}
							/>
						)}
					</>
				)}
			</div>
			{subHeader && subHeader}
			<AttachmentUpload
				{...upload}
				showTabs={showTabs}
				value={attachments}
				filter={filter}
				allowDelete={allowDelete}
				enablePDFViewer={true}
				expandedByDefault={expandedByDefault}
				allowTypeConfirmation={allowTypeConfirmation}
				baseUrl={download?.baseUrl}
				queryKey={download?.baseQueryKey}
				onDownload={(attachmentId) =>
					!!download &&
					getFileUrl({
						baseUrl: `${download.baseUrl}/${attachmentId}`,
						queryKey: [download.baseQueryKey, attachmentId, true],
						asDownload: true,
					})
				}
				enableUpload={allowUpload}
			/>
		</div>
	);
};
